/**
 * useUserPref — preferencia UI per-user persistida en backend + localStorage cache.
 *
 * Flujo:
 *   1. Mount → lee localStorage (instant) y simultáneamente GET /api/user-prefs/:key
 *   2. Si backend devuelve algo distinto → actualiza estado + cache (salvo que la persona ya cambió algo mientras tanto)
 *   3. Si el backend no tiene nada y acá hay algo guardado → lo sube una vez (recupera lo que quedó solo en este navegador)
 *   4. Cuando usuario cambia el valor → write-through:
 *        - estado optimista
 *        - localStorage inmediato
 *        - PUT backend debounced 500ms (no congestiona si toggle rápido)
 *   5. Al salir de la pantalla o cerrar la pestaña se manda lo que falte, siempre con el ÚLTIMO valor
 *
 * Genérico — sirve para guardar cualquier cosa serializable a JSON.
 *
 * Uso:
 *   const [theme, setTheme] = useUserPref("theme", "dark");
 *   const [filters, setFilters] = useUserPref("tareas:filters", {});
 *
 * Key conventions:
 *   - dominio:pagina:detalle  → ej: "collapsed:tareas:grupo"
 *   - max 120 chars
 *   - el userId NO va en el key (el backend lo agarra del req.user.id)
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../api/client.js";
import { useAuth } from "../context/AuthContext.jsx";

const DEBOUNCE_MS = 500;
const STORAGE_PREFIX = "crew:pref";

function storageKey(userId, key) {
  return `${STORAGE_PREFIX}:${userId || "anon"}:${key}`;
}

/**
 * Comprueba que el valor remoto tiene la MISMA forma primitiva que el default.
 * Descarta prefs corruptas o de otra versión del schema.
 */
function sameShape(remote, def) {
  if (def === null || def === undefined) return true; // sin restricción
  if (Array.isArray(def)) return Array.isArray(remote);
  const td = typeof def, tr = typeof remote;
  if (td === "object") return tr === "object" && !Array.isArray(remote);
  return td === tr;
}

function readLocal(userId, key, fallback) {
  try {
    const raw = localStorage.getItem(storageKey(userId, key));
    if (raw == null) return fallback;
    const parsed = JSON.parse(raw);
    return sameShape(parsed, fallback) ? parsed : fallback;
  } catch { return fallback; }
}

function writeLocal(userId, key, value) {
  try {
    localStorage.setItem(storageKey(userId, key), JSON.stringify(value));
  } catch { /* quota / private mode */ }
}

const igual = (a, b) => { try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; } };

export function useUserPref(key, defaultValue) {
  const { miembro } = useAuth();
  const userId = miembro?.id || null;

  // Estado inicial = lo que haya en localStorage para este user+key, o default
  const [value, setValueState] = useState(() => readLocal(userId, key, defaultValue));
  const [synced, setSynced] = useState(false);

  // Refs: los callbacks (timer, desmontaje, cierre de pestaña) siempre ven lo último, nunca el valor del primer render.
  const valueRef = useRef(value);          // último valor
  const ctxRef = useRef({ userId, key });  // a quién y bajo qué clave se guarda
  ctxRef.current = { userId, key };
  const timerRef = useRef(null);
  const sinGuardarRef = useRef(false);     // hay un cambio que todavía no llegó al backend
  const tocadoRef = useRef(false);         // la persona cambió algo antes de que respondiera el GET inicial

  const enviar = useCallback(() => {
    timerRef.current = null;
    const { userId: uid, key: k } = ctxRef.current;
    if (!uid || !sinGuardarRef.current) return;
    const enviado = valueRef.current;
    api.request("PUT", `/api/user-prefs/${encodeURIComponent(k)}`, { value: enviado })
      .then(() => { if (valueRef.current === enviado) sinGuardarRef.current = false; })
      .catch(e => console.warn(`[useUserPref] PUT ${k} falló (se reintenta al salir o en el próximo cambio):`, e.message));
  }, []);

  // ─── Cargar desde backend al montar / cambio de user ──────────────────────
  useEffect(() => {
    if (!userId) { setSynced(true); return; }
    let cancelled = false;

    api.request("GET", `/api/user-prefs/${encodeURIComponent(key)}`)
      .then(r => {
        if (cancelled) return;
        const remote = r?.value;
        if (remote != null && remote !== undefined && sameShape(remote, defaultValue)) {
          // Backend tiene un valor con la forma esperada → adoptar (salvo que ya cambiaron algo acá: eso es más nuevo)
          if (!tocadoRef.current) {
            valueRef.current = remote;
            setValueState(remote);
            writeLocal(userId, key, remote);
          }
        } else if (remote == null && !igual(valueRef.current, defaultValue)) {
          // El backend no tiene nada y acá sí hay algo (quedó guardado solo en este navegador): se sube una vez
          sinGuardarRef.current = true;
          enviar();
        }
        setSynced(true);
      })
      .catch(() => {
        // No hay valor o error → mantener local
        setSynced(true);
      });

    return () => { cancelled = true; };
  }, [userId, key]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Cambios hechos en otro dispositivo ───────────────────────────────────
  // Lo guardado en el celular tiene que verse en el escritorio (y al revés) sin recargar: al volver a la pestaña o a la app, y cada minuto mientras
  // está a la vista, se vuelve a preguntar. Solo se adopta lo remoto si acá no hay un cambio sin mandar (lo propio, más nuevo, gana).
  useEffect(() => {
    if (!userId) return;
    let ultimo = 0;
    const refrescar = () => {
      if (document.visibilityState !== "visible" || Date.now() - ultimo < 10000) return;
      if (sinGuardarRef.current || timerRef.current) return;
      ultimo = Date.now();
      api.request("GET", `/api/user-prefs/${encodeURIComponent(key)}`).then((r) => {
        const remote = r?.value;
        if (remote == null || !sameShape(remote, defaultValue)) return;
        if (sinGuardarRef.current || timerRef.current || igual(remote, valueRef.current)) return;
        valueRef.current = remote;
        setValueState(remote);
        writeLocal(userId, key, remote);
      }).catch(() => {});
    };
    const id = setInterval(refrescar, 60000);
    document.addEventListener("visibilitychange", refrescar);
    window.addEventListener("focus", refrescar);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", refrescar); window.removeEventListener("focus", refrescar); };
  }, [userId, key]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Setter con debounce hacia backend ────────────────────────────────────
  // Estable (no depende de `value`): un updater funcional siempre parte del último valor.
  const setValue = useCallback((next) => {
    const resolved = typeof next === "function" ? next(valueRef.current) : next;
    valueRef.current = resolved;
    tocadoRef.current = true;
    sinGuardarRef.current = true;
    setValueState(resolved);
    writeLocal(ctxRef.current.userId, ctxRef.current.key, resolved);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(enviar, DEBOUNCE_MS);
  }, [enviar]);

  // ─── Reset (borra de backend y local) ─────────────────────────────────────
  const reset = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    sinGuardarRef.current = false;
    valueRef.current = defaultValue;
    setValueState(defaultValue);
    writeLocal(userId, key, defaultValue);
    if (!userId) return;
    api.request("DELETE", `/api/user-prefs/${encodeURIComponent(key)}`)
      .catch(e => console.warn(`[useUserPref] DELETE ${key} falló:`, e.message));
  }, [userId, key, defaultValue]);

  // Lo que falte por mandar sale al desmontar la pantalla y al cerrar/ocultar la pestaña, con el último valor.
  useEffect(() => {
    const alSalir = () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      if (sinGuardarRef.current) enviar();
    };
    window.addEventListener("pagehide", alSalir);
    return () => { window.removeEventListener("pagehide", alSalir); alSalir(); };
  }, [enviar]);

  return [value, setValue, { synced, reset }];
}
